import type {CopNetworkObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/CopNetwork';
import type {CopNetworkMatNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/CopNetwork';
import type {MaterialsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import type {GeoObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {EventsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/EventsNetwork';
import type {PerspectiveCameraObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/PerspectiveCamera';
import type {RootManagerNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import {SRGBColorSpace} from 'three';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		function create_MAT(geo1: GeoObjNode) {
			var MAT = geo1.createNode('materialsNetwork');
			MAT.setName('MAT');
			function create_COP_marble(MAT: MaterialsNetworkSopNode) {
				var COP_marble = MAT.createNode('copNetwork');
				COP_marble.setName('COP_marble');
				function create_image_MARBLE_DIFFUSE(COP_marble: CopNetworkMatNode) {
					var image_MARBLE_DIFFUSE = COP_marble.createNode('image');
					image_MARBLE_DIFFUSE.setName('image_MARBLE_DIFFUSE');
					image_MARBLE_DIFFUSE.uiData.setPosition(-300, -150);
					image_MARBLE_DIFFUSE.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/marble_01/2k/diffuse.jpg'
					);
					image_MARBLE_DIFFUSE.p.tcolorSpace.set(true);
					image_MARBLE_DIFFUSE.p.colorSpace.set(SRGBColorSpace);
					image_MARBLE_DIFFUSE.p.tminFilter.set(true);
					image_MARBLE_DIFFUSE.p.tmagFilter.set(true);
					image_MARBLE_DIFFUSE.p.tanisotropy.set(true);
					image_MARBLE_DIFFUSE.p.useRendererMaxAnisotropy.set(true);
					image_MARBLE_DIFFUSE.params.postCreateSpareParams();
					image_MARBLE_DIFFUSE.params.runOnSceneLoadHooks();
					return image_MARBLE_DIFFUSE;
				}
				function create_image_MARBLE_DISP(COP_marble: CopNetworkMatNode) {
					var image_MARBLE_DISP = COP_marble.createNode('image');
					image_MARBLE_DISP.setName('image_MARBLE_DISP');
					image_MARBLE_DISP.uiData.setPosition(-300, 50);
					image_MARBLE_DISP.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/marble_01/2k/displacement.png'
					);
					image_MARBLE_DISP.p.tminFilter.set(true);
					image_MARBLE_DISP.p.tmagFilter.set(true);
					image_MARBLE_DISP.p.tanisotropy.set(true);
					image_MARBLE_DISP.p.useRendererMaxAnisotropy.set(true);
					image_MARBLE_DISP.params.postCreateSpareParams();
					image_MARBLE_DISP.params.runOnSceneLoadHooks();
					return image_MARBLE_DISP;
				}
				function create_image_MARBLE_ROUGHNESS(COP_marble: CopNetworkMatNode) {
					var image_MARBLE_ROUGHNESS = COP_marble.createNode('image');
					image_MARBLE_ROUGHNESS.setName('image_MARBLE_ROUGHNESS');
					image_MARBLE_ROUGHNESS.uiData.setPosition(-300, 250);
					image_MARBLE_ROUGHNESS.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/marble_01/2k/rough.jpg'
					);
					image_MARBLE_ROUGHNESS.p.tminFilter.set(true);
					image_MARBLE_ROUGHNESS.p.tmagFilter.set(true);
					image_MARBLE_ROUGHNESS.p.tanisotropy.set(true);
					image_MARBLE_ROUGHNESS.p.useRendererMaxAnisotropy.set(true);
					image_MARBLE_ROUGHNESS.params.postCreateSpareParams();
					image_MARBLE_ROUGHNESS.params.runOnSceneLoadHooks();
					return image_MARBLE_ROUGHNESS;
				}
				create_image_MARBLE_DIFFUSE(COP_marble);
				create_image_MARBLE_DISP(COP_marble);
				create_image_MARBLE_ROUGHNESS(COP_marble);
				COP_marble.uiData.setPosition(300, 600);
				COP_marble.params.postCreateSpareParams();
				COP_marble.params.runOnSceneLoadHooks();
				return COP_marble;
			}
			function create_COP_medieval_block(MAT: MaterialsNetworkSopNode) {
				var COP_medieval_block = MAT.createNode('copNetwork');
				COP_medieval_block.setName('COP_medieval_block');
				function create_image_MEDIEVAL_BLOCK_DIFFUSE(COP_medieval_block: CopNetworkMatNode) {
					var image_MEDIEVAL_BLOCK_DIFFUSE = COP_medieval_block.createNode('image');
					image_MEDIEVAL_BLOCK_DIFFUSE.setName('image_MEDIEVAL_BLOCK_DIFFUSE');
					image_MEDIEVAL_BLOCK_DIFFUSE.uiData.setPosition(-300, -150);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/medieval_blocks_05/2k/diffuse.jpg'
					);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.tcolorSpace.set(true);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.colorSpace.set(SRGBColorSpace);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.tminFilter.set(true);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.tmagFilter.set(true);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.tanisotropy.set(true);
					image_MEDIEVAL_BLOCK_DIFFUSE.p.useRendererMaxAnisotropy.set(true);
					image_MEDIEVAL_BLOCK_DIFFUSE.params.postCreateSpareParams();
					image_MEDIEVAL_BLOCK_DIFFUSE.params.runOnSceneLoadHooks();
					return image_MEDIEVAL_BLOCK_DIFFUSE;
				}
				function create_image_MEDIEVAL_BLOCK_DISP(COP_medieval_block: CopNetworkMatNode) {
					var image_MEDIEVAL_BLOCK_DISP = COP_medieval_block.createNode('image');
					image_MEDIEVAL_BLOCK_DISP.setName('image_MEDIEVAL_BLOCK_DISP');
					image_MEDIEVAL_BLOCK_DISP.uiData.setPosition(-300, 50);
					image_MEDIEVAL_BLOCK_DISP.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/medieval_blocks_05/2k/displacement.png'
					);
					image_MEDIEVAL_BLOCK_DISP.p.tminFilter.set(true);
					image_MEDIEVAL_BLOCK_DISP.p.tmagFilter.set(true);
					image_MEDIEVAL_BLOCK_DISP.p.tanisotropy.set(true);
					image_MEDIEVAL_BLOCK_DISP.p.useRendererMaxAnisotropy.set(true);
					image_MEDIEVAL_BLOCK_DISP.params.postCreateSpareParams();
					image_MEDIEVAL_BLOCK_DISP.params.runOnSceneLoadHooks();
					return image_MEDIEVAL_BLOCK_DISP;
				}
				function create_image_MEDIEVAL_BLOCK_ROUGHNESS(COP_medieval_block: CopNetworkMatNode) {
					var image_MEDIEVAL_BLOCK_ROUGHNESS = COP_medieval_block.createNode('image');
					image_MEDIEVAL_BLOCK_ROUGHNESS.setName('image_MEDIEVAL_BLOCK_ROUGHNESS');
					image_MEDIEVAL_BLOCK_ROUGHNESS.uiData.setPosition(-300, 250);
					image_MEDIEVAL_BLOCK_ROUGHNESS.p.url.set(
						'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/resources/polyhaven.com/medieval_blocks_05/2k/rough.jpg'
					);
					image_MEDIEVAL_BLOCK_ROUGHNESS.p.tminFilter.set(true);
					image_MEDIEVAL_BLOCK_ROUGHNESS.p.tmagFilter.set(true);
					image_MEDIEVAL_BLOCK_ROUGHNESS.p.tanisotropy.set(true);
					image_MEDIEVAL_BLOCK_ROUGHNESS.p.useRendererMaxAnisotropy.set(true);
					image_MEDIEVAL_BLOCK_ROUGHNESS.params.postCreateSpareParams();
					image_MEDIEVAL_BLOCK_ROUGHNESS.params.runOnSceneLoadHooks();
					return image_MEDIEVAL_BLOCK_ROUGHNESS;
				}
				create_image_MEDIEVAL_BLOCK_DIFFUSE(COP_medieval_block);
				create_image_MEDIEVAL_BLOCK_DISP(COP_medieval_block);
				create_image_MEDIEVAL_BLOCK_ROUGHNESS(COP_medieval_block);
				COP_medieval_block.uiData.setPosition(300, 800);
				COP_medieval_block.params.postCreateSpareParams();
				COP_medieval_block.params.runOnSceneLoadHooks();
				return COP_medieval_block;
			}
			function create_meshStandard1(MAT: MaterialsNetworkSopNode) {
				var meshStandard1 = MAT.createNode('meshStandard');
				meshStandard1.setName('meshStandard1');
				meshStandard1.uiData.setPosition(0, 0);
				meshStandard1.p.useMap.set(true);
				meshStandard1.p.map.set('../../../COP/imageUv');
				meshStandard1.p.useEnvMap.set(true);
				meshStandard1.p.envMap.set('../../../COP/envMap');
				meshStandard1.p.metalness.set(1);
				meshStandard1.p.roughness.set(0.07);
				meshStandard1.params.postCreateSpareParams();
				meshStandard1.params.runOnSceneLoadHooks();
				return meshStandard1;
			}
			function create_meshStandard2(MAT: MaterialsNetworkSopNode) {
				var meshStandard2 = MAT.createNode('meshStandard');
				meshStandard2.setName('meshStandard2');
				meshStandard2.uiData.setPosition(0, 200);
				meshStandard2.p.useMap.set(true);
				meshStandard2.p.map.set('../../../COP/imageUv');
				meshStandard2.p.useEnvMap.set(true);
				meshStandard2.p.envMap.set('../../../COP/envMap');
				meshStandard2.p.metalness.set(1);
				meshStandard2.p.roughness.set(0.34);
				meshStandard2.params.postCreateSpareParams();
				meshStandard2.params.runOnSceneLoadHooks();
				return meshStandard2;
			}
			function create_meshStandard3(MAT: MaterialsNetworkSopNode) {
				var meshStandard3 = MAT.createNode('meshStandard');
				meshStandard3.setName('meshStandard3');
				meshStandard3.uiData.setPosition(0, 400);
				meshStandard3.p.useMap.set(true);
				meshStandard3.p.map.set('../../../COP/imageUv');
				meshStandard3.p.useEnvMap.set(true);
				meshStandard3.p.envMap.set('../../../COP/envMap');
				meshStandard3.params.postCreateSpareParams();
				meshStandard3.params.runOnSceneLoadHooks();
				return meshStandard3;
			}
			function create_meshStandard4(MAT: MaterialsNetworkSopNode) {
				var meshStandard4 = MAT.createNode('meshStandard');
				meshStandard4.setName('meshStandard4');
				meshStandard4.uiData.setPosition(0, 600);
				meshStandard4.p.useMap.set(true);
				meshStandard4.p.map.set('../COP_marble/image_MARBLE_DIFFUSE');
				meshStandard4.p.useDisplacementMap.set(true);
				meshStandard4.p.displacementMap.set('../COP_marble/image_MARBLE_DISP');
				meshStandard4.p.displacementScale.set(0.02);
				meshStandard4.p.useEnvMap.set(true);
				meshStandard4.p.envMap.set('../../../COP/envMap');
				meshStandard4.p.useRoughnessMap.set(true);
				meshStandard4.p.roughnessMap.set('../COP_marble/image_MARBLE_ROUGHNESS');
				meshStandard4.params.postCreateSpareParams();
				meshStandard4.params.runOnSceneLoadHooks();
				return meshStandard4;
			}
			function create_meshStandard5(MAT: MaterialsNetworkSopNode) {
				var meshStandard5 = MAT.createNode('meshStandard');
				meshStandard5.setName('meshStandard5');
				meshStandard5.uiData.setPosition(0, 800);
				meshStandard5.p.useMap.set(true);
				meshStandard5.p.map.set('../COP_medieval_block/image_MEDIEVAL_BLOCK_DIFFUSE');
				meshStandard5.p.useDisplacementMap.set(true);
				meshStandard5.p.displacementMap.set('../COP_medieval_block/image_MEDIEVAL_BLOCK_DISP');
				meshStandard5.p.displacementScale.set(0.25);
				meshStandard5.p.useEnvMap.set(true);
				meshStandard5.p.envMap.set('../../../COP/envMap');
				meshStandard5.p.useRoughnessMap.set(true);
				meshStandard5.p.roughnessMap.set('../COP_medieval_block/image_MEDIEVAL_BLOCK_ROUGHNESS');
				meshStandard5.params.postCreateSpareParams();
				meshStandard5.params.runOnSceneLoadHooks();
				return meshStandard5;
			}
			create_COP_marble(MAT);
			create_COP_medieval_block(MAT);
			create_meshStandard1(MAT);
			create_meshStandard2(MAT);
			create_meshStandard3(MAT);
			create_meshStandard4(MAT);
			create_meshStandard5(MAT);
			MAT.uiData.setPosition(-300, 150);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return MAT;
		}
		function create_material1(geo1: GeoObjNode) {
			var material1 = geo1.createNode('material');
			material1.setName('material1');
			material1.uiData.setPosition(-50, 150);
			material1.p.material.set('../MAT/meshStandard1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return material1;
		}
		function create_material2(geo1: GeoObjNode) {
			var material2 = geo1.createNode('material');
			material2.setName('material2');
			material2.uiData.setPosition(150, 150);
			material2.p.material.set('../MAT/meshStandard2');
			material2.params.postCreateSpareParams();
			material2.params.runOnSceneLoadHooks();
			return material2;
		}
		function create_material3(geo1: GeoObjNode) {
			var material3 = geo1.createNode('material');
			material3.setName('material3');
			material3.uiData.setPosition(350, 150);
			material3.p.material.set('../MAT/meshStandard3');
			material3.params.postCreateSpareParams();
			material3.params.runOnSceneLoadHooks();
			return material3;
		}
		function create_material4(geo1: GeoObjNode) {
			var material4 = geo1.createNode('material');
			material4.setName('material4');
			material4.uiData.setPosition(600, 150);
			material4.p.material.set('../MAT/meshStandard4');
			material4.params.postCreateSpareParams();
			material4.params.runOnSceneLoadHooks();
			return material4;
		}
		function create_material5(geo1: GeoObjNode) {
			var material5 = geo1.createNode('material');
			material5.setName('material5');
			material5.uiData.setPosition(800, 150);
			material5.p.material.set('../MAT/meshStandard5');
			material5.params.postCreateSpareParams();
			material5.params.runOnSceneLoadHooks();
			return material5;
		}
		function create_merge1(geo1: GeoObjNode) {
			var merge1 = geo1.createNode('merge');
			merge1.setName('merge1');
			merge1.uiData.setPosition(350, 500);
			merge1.flags.display.set(true);
			merge1.p.inputsCount.set(5);
			merge1.io.inputs.setCount(1, 5);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return merge1;
		}
		function create_sphere1(geo1: GeoObjNode) {
			var sphere1 = geo1.createNode('sphere');
			sphere1.setName('sphere1');
			sphere1.uiData.setPosition(350, -300);
			sphere1.p.radius.set(0.92);
			sphere1.p.resolution.set([60, 60]);
			sphere1.params.postCreateSpareParams();
			sphere1.params.runOnSceneLoadHooks();
			return sphere1;
		}
		function create_transform1(geo1: GeoObjNode) {
			var transform1 = geo1.createNode('transform');
			transform1.setName('transform1');
			transform1.uiData.setPosition(-50, 0);
			transform1.p.t.set([0, 1.0741628078914935, 2]);
			transform1.params.postCreateSpareParams();
			transform1.params.runOnSceneLoadHooks();
			return transform1;
		}
		function create_transform2(geo1: GeoObjNode) {
			var transform2 = geo1.createNode('transform');
			transform2.setName('transform2');
			transform2.uiData.setPosition(150, 0);
			transform2.p.t.set([0, 1.0741628078914935, 0]);
			transform2.params.postCreateSpareParams();
			transform2.params.runOnSceneLoadHooks();
			return transform2;
		}
		function create_transform3(geo1: GeoObjNode) {
			var transform3 = geo1.createNode('transform');
			transform3.setName('transform3');
			transform3.uiData.setPosition(350, 0);
			transform3.p.t.set([0, 1.0741628078914935, -2]);
			transform3.params.postCreateSpareParams();
			transform3.params.runOnSceneLoadHooks();
			return transform3;
		}
		function create_transform4(geo1: GeoObjNode) {
			var transform4 = geo1.createNode('transform');
			transform4.setName('transform4');
			transform4.uiData.setPosition(600, 0);
			transform4.p.t.set([0, 1.0741628078914935, -4]);
			transform4.params.postCreateSpareParams();
			transform4.params.runOnSceneLoadHooks();
			return transform4;
		}
		function create_transform5(geo1: GeoObjNode) {
			var transform5 = geo1.createNode('transform');
			transform5.setName('transform5');
			transform5.uiData.setPosition(800, 0);
			transform5.p.t.set([0, 1.0741628078914935, -6]);
			transform5.params.postCreateSpareParams();
			transform5.params.runOnSceneLoadHooks();
			return transform5;
		}
		create_MAT(geo1);
		var material1 = create_material1(geo1);
		var material2 = create_material2(geo1);
		var material3 = create_material3(geo1);
		var material4 = create_material4(geo1);
		var material5 = create_material5(geo1);
		var merge1 = create_merge1(geo1);
		var sphere1 = create_sphere1(geo1);
		var transform1 = create_transform1(geo1);
		var transform2 = create_transform2(geo1);
		var transform3 = create_transform3(geo1);
		var transform4 = create_transform4(geo1);
		var transform5 = create_transform5(geo1);
		material1.setInput(0, transform1);
		material2.setInput(0, transform2);
		material3.setInput(0, transform3);
		material4.setInput(0, transform4);
		material5.setInput(0, transform5);
		merge1.setInput(0, material1);
		merge1.setInput(1, material2);
		merge1.setInput(2, material3);
		merge1.setInput(3, material4);
		merge1.setInput(4, material5);
		transform1.setInput(0, sphere1);
		transform2.setInput(0, sphere1);
		transform3.setInput(0, sphere1);
		transform4.setInput(0, sphere1);
		transform5.setInput(0, sphere1);
		geo1.uiData.setPosition(-50, -150);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return geo1;
	}
	function create_grid(parentNode: RootManagerNode) {
		var grid = parentNode.createNode('geo');
		grid.setName('grid');
		function create_planeHelper1(grid: GeoObjNode) {
			var planeHelper1 = grid.createNode('planeHelper');
			planeHelper1.setName('planeHelper1');
			planeHelper1.uiData.setPosition(0, 0);
			planeHelper1.flags.display.set(true);
			planeHelper1.params.postCreateSpareParams();
			planeHelper1.params.runOnSceneLoadHooks();
			return planeHelper1;
		}
		create_planeHelper1(grid);
		grid.uiData.setPosition(-50, -250);
		grid.flags.display.set(true);
		grid.params.postCreateSpareParams();
		grid.params.runOnSceneLoadHooks();
		return grid;
	}
	function create_perspectiveCamera1(parentNode: RootManagerNode) {
		var perspectiveCamera1 = parentNode.createNode('perspectiveCamera');
		perspectiveCamera1.setName('perspectiveCamera1');
		function create_events1(perspectiveCamera1: PerspectiveCameraObjNode) {
			var events1 = perspectiveCamera1.createNode('eventsNetwork');
			events1.setName('events1');
			function create_cameraOrbitControls1(events1: EventsNetworkSopNode) {
				var cameraOrbitControls1 = events1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				cameraOrbitControls1.uiData.setPosition(150, 50);
				cameraOrbitControls1.p.target.set([-0.36089647523045165, 1.035331232279625, -1.583357436149622]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return cameraOrbitControls1;
			}
			create_cameraOrbitControls1(events1);
			events1.uiData.setPosition(-200, 50);
			events1.params.postCreateSpareParams();
			events1.params.runOnSceneLoadHooks();
			return events1;
		}
		create_events1(perspectiveCamera1);
		perspectiveCamera1.uiData.setPosition(-200, 100);
		perspectiveCamera1.flags.display.set(true);
		perspectiveCamera1.p.t.set([5.5806932779151825, 2.0221492327126347, -1.1112275138148067]);
		perspectiveCamera1.p.r.set([-64.41408828886027, 79.57122339521676, 64.04040093267949]);
		perspectiveCamera1.p.controls.set('./events1/cameraOrbitControls1');
		perspectiveCamera1.params.postCreateSpareParams();
		perspectiveCamera1.params.runOnSceneLoadHooks();
		return perspectiveCamera1;
	}
	function create_COP(parentNode: RootManagerNode) {
		var COP = parentNode.createNode('copNetwork');
		COP.setName('COP');
		function create_envMap(COP: CopNetworkObjNode) {
			var envMap = COP.createNode('envMap');
			envMap.setName('envMap');
			envMap.uiData.setPosition(50, 200);
			envMap.params.postCreateSpareParams();
			envMap.params.runOnSceneLoadHooks();
			return envMap;
		}
		function create_imageEnv(COP: CopNetworkObjNode) {
			var imageEnv = COP.createNode('imageEXR');
			imageEnv.setName('imageEnv');
			imageEnv.uiData.setPosition(50, 100);
			imageEnv.p.url.set(
				'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/piz_compressed.exr'
			);
			imageEnv.params.postCreateSpareParams();
			imageEnv.params.runOnSceneLoadHooks();
			return imageEnv;
		}
		function create_imageUv(COP: CopNetworkObjNode) {
			var imageUv = COP.createNode('image');
			imageUv.setName('imageUv');
			imageUv.uiData.setPosition(-100, 100);
			imageUv.p.url.set('https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/uv.jpg');
			imageUv.p.tflipY.set(true);
			imageUv.params.postCreateSpareParams();
			imageUv.params.runOnSceneLoadHooks();
			return imageUv;
		}
		var envMap = create_envMap(COP);
		var imageEnv = create_imageEnv(COP);
		create_imageUv(COP);
		envMap.setInput(0, imageEnv);
		COP.uiData.setPosition(-200, 200);
		COP.params.postCreateSpareParams();
		COP.params.runOnSceneLoadHooks();
		return COP;
	}
	function create_lights(parentNode: RootManagerNode) {
		var lights = parentNode.createNode('geo');
		lights.setName('lights');
		function create_hemisphereLight1(lights: GeoObjNode) {
			var hemisphereLight1 = lights.createNode('hemisphereLight');
			hemisphereLight1.setName('hemisphereLight1');
			hemisphereLight1.uiData.setPosition(-100, 0);
			hemisphereLight1.params.postCreateSpareParams();
			hemisphereLight1.params.runOnSceneLoadHooks();
			return hemisphereLight1;
		}
		function create_merge1(lights: GeoObjNode) {
			var merge1 = lights.createNode('merge');
			merge1.setName('merge1');
			merge1.uiData.setPosition(50, 300);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return merge1;
		}
		function create_polarTransform1(lights: GeoObjNode) {
			var polarTransform1 = lights.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			polarTransform1.uiData.setPosition(150, 50);
			polarTransform1.p.longitude.set(7.2);
			polarTransform1.p.latitude.set(46.8);
			polarTransform1.p.depth.set(5.8);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return polarTransform1;
		}
		function create_spotLight1(lights: GeoObjNode) {
			var spotLight1 = lights.createNode('spotLight');
			spotLight1.setName('spotLight1');
			spotLight1.uiData.setPosition(150, -150);
			spotLight1.params.postCreateSpareParams();
			spotLight1.params.runOnSceneLoadHooks();
			return spotLight1;
		}
		var hemisphereLight1 = create_hemisphereLight1(lights);
		var merge1 = create_merge1(lights);
		var polarTransform1 = create_polarTransform1(lights);
		var spotLight1 = create_spotLight1(lights);
		merge1.setInput(0, hemisphereLight1);
		merge1.setInput(1, polarTransform1);
		polarTransform1.setInput(0, spotLight1);
		lights.uiData.setPosition(-50, -50);
		lights.flags.display.set(true);
		lights.params.postCreateSpareParams();
		lights.params.runOnSceneLoadHooks();
		return lights;
	}
	var geo1 = create_geo1(root);
	create_grid(root);
	var perspectiveCamera1 = create_perspectiveCamera1(root);
	create_COP(root);
	create_lights(root);
	return {
		scene,
		cameraPath: perspectiveCamera1.path(),
		focusedNodes: [geo1.nodesByType('material')[0]],
		autoLayout: false,
	};
}
// export const matMeshStandard = [
// 	new Example({
// 		name: `${ExamplePrefix.BY_NODE}/mat/meshStandard`,
// 		createData,
// 		relatedNodes: [MeshStandardMatNode],
// 	}),
// ];
